
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
// import { initPerformanceOptimizations } from './lib/performance-optimizations'

// Enhanced global error handler for production debugging
window.addEventListener('error', (event) => {
  const errorMsg = `Global error: ${event.error?.message || event.message}`;
  console.error('🚨 MAIN:', errorMsg);
  
  // Add to debug logs if available
  if (typeof window !== 'undefined' && window.addDebugLog) {
    window.addDebugLog(errorMsg, 'error');
  }
  
  // Prevent complete app failure from third-party script issues
  if (event.filename && 
      !event.filename.includes('usercentrics') && 
      !event.filename.includes('hotjar') &&
      !event.message.includes('Script error')) {
    const rootElement = document.getElementById("root");
    
    if (rootElement && !rootElement.children.length) {
      console.warn('🚨 MAIN: Showing error notification due to application error');
      rootElement.innerHTML += `
        <div style="position: fixed; bottom: 20px; right: 20px; background: #fee; border: 1px solid #f99; 
                    padding: 10px; border-radius: 5px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); max-width: 80%; z-index: 9999;">
          <p style="margin: 0; font-size: 14px;">⚠️ An error occurred, but the site is still running. 
             If you're experiencing issues, please try refreshing the page.</p>
        </div>
      `;
    }
  }
});

// Enhanced Promise error handling
window.addEventListener('unhandledrejection', (event) => {
  const errorMsg = `Unhandled Promise rejection: ${event.reason?.message || event.reason}`;
  console.error('🚨 MAIN:', errorMsg);
  if (typeof window !== 'undefined' && window.addDebugLog) {
    window.addDebugLog(errorMsg, 'error');
  }
});

// Get the root element
const rootElement = document.getElementById("root");

if (!rootElement) {
  console.error("Failed to find the root element");
  const fallbackElement = document.getElementById("fallback-content");
  if (fallbackElement) {
    fallbackElement.style.display = 'flex';
  }
} else {
  // Create concurrent root for better performance
  const root = createRoot(rootElement);
  
  try {
    console.log('✅ MAIN: Starting app initialization');
    if (typeof window !== 'undefined' && window.addDebugLog) {
      window.addDebugLog('Main.tsx: Starting app initialization');
    }
    
    // Temporarily disable performance optimizations for production stability
    // initPerformanceOptimizations();
    
    // Mark the time when app starts rendering for performance monitoring
    if (window.performance && window.performance.mark) {
      window.performance.mark('app-render-start');
      if (typeof window !== 'undefined' && window.addDebugLog) {
        window.addDebugLog('Performance mark: app-render-start');
      }
      
      // LCP observation
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          if (lastEntry && lastEntry.element) {
            console.log('✅ MAIN: LCP detected:', lastEntry.startTime, 'Element:', lastEntry.element);
            window.performance.mark('lcp-detected');
            if (typeof window !== 'undefined' && window.addDebugLog) {
              window.addDebugLog(`LCP detected: ${Math.round(lastEntry.startTime)}ms`);
            }
          }
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      }
    }
    
    // Hide initial loading indicator immediately
    const initialLoader = document.querySelector('.initial-loading');
    if (initialLoader) {
      initialLoader.remove();
      if (typeof window !== 'undefined' && window.addDebugLog) {
        window.addDebugLog('Initial loader removed');
      }
    }
    
    // Render the app with production error boundary
    console.log('✅ MAIN: About to render React app');
    if (typeof window !== 'undefined' && window.addDebugLog) {
      window.addDebugLog('About to render React app');
    }
    root.render(<App />);
    
    console.log("✅ MAIN: React app successfully rendered");
    if (typeof window !== 'undefined' && window.addDebugLog) {
      window.addDebugLog('React app successfully rendered');
    }
    
    // Production debugging info
    if ((window as any).productionErrors) {
      console.log('Production debugging enabled. Errors will be logged to window.productionErrors');
    }
    
  } catch (error) {
    console.error("Failed to render the app:", error);
    
    const fallbackElement = document.getElementById("fallback-content");
    if (fallbackElement) {
      fallbackElement.style.display = 'flex';
      rootElement.style.display = 'none';
    } else {
      rootElement.innerHTML = `
        <div style="padding: 20px; text-align: center;">
          <h2>Something went wrong</h2>
          <p>Please try refreshing the page or contact support if the issue persists.</p>
          <a href="https://loenaluxurysuites.reserve-online.net/" style="display: inline-block; margin-top: 20px; padding: 10px 20px; background-color: #000; color: hsl(38, 30%, 65%); text-decoration: none; border-radius: 5px;">Book Now</a>
        </div>
      `;
    }
  }
}

// Service worker registration removed - file doesn't exist
// PWA functionality handled by VitePWA plugin in production builds

// Reduced heartbeat frequency
setInterval(() => {
  console.log("Website heartbeat check: running at", new Date().toISOString());
}, 60000);
